import { useCallback, useMemo, useState } from 'react'
import { TableReportWithdrawsColumnsDefinition } from './components/TableReportWithdrawsColumnsDefinition'
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import { format } from 'date-fns'
import { useReportWithdraws } from '@/app/hooks/useReportWithdraws'
import * as XLSX from 'xlsx'
import {
  ReportWithdraws,
  ReportWithdrawsParams,
} from '@/app/services/reportService/reportWithdraws'
import { reportService } from '@/app/services/reportService'
import { useAuth } from '@/app/hooks/useAuth'
import { FiltersType } from '../components/FilterReports'
import { obscure } from '@/app/utils/obscure'
import { fetchAllReportData } from '@/app/utils/fetchAllReportData'
import { useToast } from '@/view/components/ui/use-toast'

const AMOUNT_REPORTS_FETCH = Number(import.meta.env.VITE_AMOUNT_REPORTS_FETCH)

export const useReportWithdrawsController = () => {
  const [exportProgress, setExportProgress] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const { data, isFetching, isLoading, setParams, params } =
    useReportWithdraws()

  const { isLeader, isAdmin, isManager, isSupervisor } = useAuth()
  const { toast } = useToast()
  const { columns } = TableReportWithdrawsColumnsDefinition()

  const table = useReactTable({
    data: data?.withdraws || [],
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: data?.pagination.limit || 100,
      },
    },
  })

  const fetchAllData = useCallback(async (): Promise<ReportWithdraws[]> => {
    if (!data) return []

    setIsExporting(true)
    setExportProgress(0)

    try {
      return await fetchAllReportData<ReportWithdraws>({
        total: data.pagination.total,
        batchSize: AMOUNT_REPORTS_FETCH,
        onProgress: setExportProgress,
        fetchBatch: async ({ skip, limit }) => {
          const exportParams: ReportWithdrawsParams = { ...params, limit, skip }
          const batchData = await reportService.reportWithdraws(exportParams)
          return batchData.withdraws
        },
      })
    } finally {
      setIsExporting(false)
    }
  }, [data, params])

  const handleExportToExcel = useCallback(async () => {
    try {
      const fetchedData = await fetchAllData()

      const formattedData = fetchedData.map((withdraw) => ({
        cpf: !isAdmin ? obscure(withdraw.cpf) : withdraw.cpf || '',
        seller: withdraw.user || '',
        team: withdraw.team || 'Sem equipe',
        manager: withdraw.manager || 'Sem gerente',
        supervisor: withdraw.supervisor || 'Sem supervisor',
        leader: withdraw.leader || 'Sem Líder',
        value: +(withdraw.value / 100).toFixed(2) || 0,
        pixFee: withdraw.pix_fee ? +(withdraw.pix_fee / 100).toFixed(2) : 0,
        date: format(withdraw.updatedAt, 'dd/MM/yyyy, HH:mm') || '',
        status: withdraw.status === 'PAID' ? 'Pago' : 'Pendente',
      }))

      if (formattedData.length > 0) {
        const workbook = XLSX.utils.book_new()
        const worksheet = XLSX.utils.json_to_sheet(formattedData)
        XLSX.utils.book_append_sheet(workbook, worksheet, 'ExportedReport')
        XLSX.utils.sheet_add_aoa(worksheet, [
          [
            'Cpf',
            'Vendedor',
            'Equipe',
            'Gerente',
            'Supervisor',
            'Líder',
            'Valor',
            'Taxa',
            'Data',
            'Status',
          ],
        ])
        XLSX.writeFile(workbook, 'withdraws-reports.xlsx')
      }
    } catch (error) {
      console.error('Error exporting withdraws report:', error)
      toast({
        title: 'Não foi possível exportar o relatório',
        description:
          'Alguns dados não puderam ser carregados. Tente novamente.',
        variant: 'destructive',
      })
    } finally {
      setIsExporting(false)
    }
  }, [fetchAllData, isAdmin, toast])

  const getPageIndex = () => {
    if (!data) return 0
    return Math.floor(data.pagination.skip / data.pagination.limit) + 1
  }

  const getPageCount = () => {
    if (!data) return 0
    return Math.ceil(data.pagination.total / data.pagination.limit)
  }

  const handleNextPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit + 1
    if (getPageCount() === getPageIndex()) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const handlePreviousPage = () => {
    if (!data) return
    const newPage = data.pagination.skip / data.pagination.limit - 1
    if (newPage < 0) return
    const skipValue = newPage * data.pagination.limit
    setParams((p) => ({ ...p, skip: skipValue }))
  }

  const filters: FiltersType[] = useMemo(() => {
    const result: FiltersType[] = ['Date']

    if (isAdmin)
      result.push('Managers', 'Supervisors', 'Leaders', 'Teams', 'Sellers')
    if (isManager) result.push('Supervisors', 'Leaders', 'Teams', 'Sellers')
    if (isSupervisor) result.push('Leaders', 'Teams', 'Sellers')
    if (isLeader) result.push('Sellers')

    result.push('Value')
    return result
  }, [isAdmin, isManager, isSupervisor, isLeader])

  return {
    table,
    filters,
    handleNextPage,
    handlePreviousPage,
    getPageIndex,
    getPageCount,
    isFetching,
    isLoading,
    setParams,
    isExporting,
    exportProgress,
    handleExportToExcel,
  }
}
